import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { EyeIcon } from "components/icons";
import { FieldActionIcon, FieldText } from "components/Table/Field";

import { useGetNotes } from "../UseCase/useGetNotes";
import { useTranslationContext } from "context/TranslationContext";
import { formatDate } from "helpers/formatDate";
import { replaceSegmentsFromRoutes } from "helpers/replaceSegmentsFromRoutes";
import { useTableQueryParams } from "hooks/useTableQueryParams";

import { TGetNotesFilter } from "../Model/types";
import {
  ENotesEntity,
  EPipelineSlug,
  TGetNotesRes,
} from "@type/api/management/types";
import {
  TFinalSort,
  TSort,
  TTabeField,
  TTableRecord,
} from "components/Table/types";
import { EAccount } from "types/index";
import {
  BUSINESS_UNIT_TABS,
  DEFAULT_FILTER_NOTES,
  DEFAULT_SORT_NOTES,
  PERSONAL_ACCOUNT_TABS,
  SORT_MAP_NOTES,
} from "../Model/constants";
import { DEFAULT_PAGINATION } from "components/Table/constants";
import {
  buSlugParam,
  corpSlugParam,
  personalSlugParam,
  routePaths,
} from "routes/constants";

type TUsePastNotesControllerParams = {
  pipelineSlug?: EPipelineSlug;
  entityType?: ENotesEntity;
  label?: ReactNode;
};

export const usePastNotesController = ({
  pipelineSlug = EPipelineSlug.INSIDE_SALES,
  entityType = ENotesEntity.BUSINESS_UNIT,
  label,
}: TUsePastNotesControllerParams) => {
  const navigate = useNavigate();
  const { t } = useTranslationContext();
  const { personalSlug, buSlug, corporationSlug } = useParams();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedNote, setSelectedNote] =
    useState<TGetNotesRes["data"][number]>();

  const {
    selectedTab,
    profileName,
    profileImage,
    profileUniqueId,
    profileAccountType,
    profileUrlPath,
    profileSlug,
  } = useMemo(() => {
    switch (pipelineSlug) {
      case EPipelineSlug.INSIDE_SALES:
        return {
          selectedTab: "insideSales",
          profileName: selectedNote?.staff?.fullName,
          profileImage: selectedNote?.staff?.profilePicture,
          profileUniqueId: selectedNote?.staff?.idUnique,
          profileAccountType: EAccount.STAFF,
          profileUrlPath: routePaths.root.bu.root.pBuSlug.member.pPersonal.root,
          profileSlug: personalSlugParam,
        };
      case EPipelineSlug.FIELD_SALES:
        return {
          selectedTab: "fieldSales",
          profileName:
            selectedNote?.ticket?.clientEngagementData?.corporation?.name,
          profileUniqueId:
            selectedNote?.ticket?.clientEngagementData?.corporation?.idUnique,
          profileImage:
            selectedNote?.ticket?.clientEngagementData?.corporation
              ?.profilePicture,
          profileAccountType: EAccount.CORPORATION,
          profileUrlPath: routePaths.root.corp.root.pCorpSlug.root,
          profileSlug: corpSlugParam,
        };
      case EPipelineSlug.CUSTOMER_SUCCESS:
        return {
          selectedTab: "customerSuccess",
          profileName: selectedNote?.staff?.fullName,
          profileImage: selectedNote?.staff?.profilePicture,
          profileUniqueId: selectedNote?.staff?.idUnique,
          profileAccountType: EAccount.STAFF,
          profileUrlPath: routePaths.root.bu.root.pBuSlug.member.pPersonal.root,
          profileSlug: personalSlugParam,
        };
      default:
        return {};
    }
  }, [pipelineSlug, selectedNote]);

  const { entityId, tab } = useMemo(() => {
    switch (entityType) {
      case ENotesEntity.BUSINESS_UNIT:
        return { entityId: buSlug, tab: BUSINESS_UNIT_TABS };
      case ENotesEntity.STAFF:
        return { entityId: personalSlug, tab: PERSONAL_ACCOUNT_TABS };
      case ENotesEntity.CORPORATION:
        return { entityId: corporationSlug };
      default:
        return {};
    }
  }, [entityType, buSlug, personalSlug, corporationSlug]);

  const { searchParams, onSetSearchParams } =
    useTableQueryParams<TGetNotesFilter>({
      sortMap: SORT_MAP_NOTES,
      defaultData: {
        sort: DEFAULT_SORT_NOTES,
        filter: {
          ...DEFAULT_FILTER_NOTES,
          idBu: buSlug || "",
        },
      },
    });

  const sort = useMemo(() => {
    const splitedSort = searchParams?.constructedSort?.split(" ");
    if (splitedSort.length < 2) return {};

    return {
      sortKey: splitedSort[0],
      sortType: splitedSort[1],
    };
  }, [searchParams?.constructedSort]);

  const { notes, isLoadingGetNotes } = useGetNotes({
    params: {
      idBu: buSlug || "",
      data: {
        filter: {
          ...searchParams?.filter,
          idPipelineStatus:
            searchParams?.filter?.idPipelineStatus?.split(",") || [],
          idTicket: searchParams?.filter?.idTicket?.split(",") || [],
          staffType: searchParams?.filter?.staffType?.split(",") || [],
          interviewType: searchParams?.filter?.interviewType?.split(",") || [],
        },
        ...searchParams?.page,
        ...sort,
        pipelineSlug,
      },
      urlData: {
        entityType,
        entityId: entityId || "",
      },
    },
    onSuccess: (res) => {
      setPagination((prev) => ({
        ...prev,
        currentPage: res.page,
        itemPerPage: res.pageSize,
        totalItem: res.total,
      }));
    },
  });

  const handleViewProfile = () => {
    if (!profileUniqueId || !profileUrlPath || !profileSlug) return;

    let routeConfig;
    const buUniqueId = selectedNote?.ticket?.businessUnit?.idUnique || buSlug;

    if (pipelineSlug === EPipelineSlug.FIELD_SALES) {
      routeConfig = {
        params: [profileSlug],
        replacer: [profileUniqueId],
      };
    } else if (buUniqueId) {
      routeConfig = {
        params: [buSlugParam, profileSlug],
        replacer: [buUniqueId, profileUniqueId],
      };
    }

    if (routeConfig) {
      navigate(
        replaceSegmentsFromRoutes({ route: profileUrlPath, ...routeConfig })
      );
    }
  };

  const handleChangePage = useCallback(
    (newPage: number) => {
      onSetSearchParams({
        page: newPage,
      });
    },
    [onSetSearchParams]
  );

  const handleClickAction = useCallback(
    (record: TTableRecord, _actionType: string) => {
      setSelectedNote(record as TGetNotesRes["data"][number]);
    },
    []
  );

  const handleChangeDate = (start: Date | undefined, end: Date | undefined) => {
    onSetSearchParams({
      startDate: start ? formatDate(new Date(start)) : undefined,
      endDate: end ? formatDate(new Date(end)) : undefined,
    });
  };

  const handleChangeTab = useCallback(
    (key: string) => {
      const selectedTab = (tab as any)?.[key];
      if (!selectedTab?.path) return;

      let params: string[] | undefined;
      let replacer: string[] | undefined;

      if (buSlug && entityType === ENotesEntity.BUSINESS_UNIT) {
        params = [buSlugParam];
        replacer = [buSlug];
      } else if (personalSlug && entityType === ENotesEntity.STAFF) {
        params = [personalSlugParam];
        replacer = [personalSlug];
      }

      if (params && replacer) {
        navigate(
          replaceSegmentsFromRoutes({
            route: selectedTab.path,
            params,
            replacer,
          })
        );
      }
    },
    [buSlug, entityType, navigate, personalSlug, tab]
  );

  const handleChangePerPage = useCallback(
    (perPage: number) => {
      onSetSearchParams({
        pageSize: perPage,
      });
    },
    [onSetSearchParams]
  );

  const fields: TTabeField[] = [
    {
      title: t("date_and_time"),
      key: "dateAndTime",
      width: "13%",
      sort: searchParams?.sort?.["dateAndTime"],
      component: FieldText,
    },
    ...(pipelineSlug !== EPipelineSlug.FIELD_SALES
      ? [
          {
            title: t("staff_type"),
            key: "staffType",
            width:
              pipelineSlug === EPipelineSlug.CUSTOMER_SUCCESS ? "11%" : "16%",
            sort: searchParams?.sort?.["staffType"],
            component: FieldText,
          } as TTabeField,
        ]
      : []),
    {
      title: t("pipeline_status"),
      key: "pipelineStatusName",
      width: pipelineSlug === EPipelineSlug.FIELD_SALES ? "30%" : "18%",
      sort: searchParams?.sort?.["pipelineStatusName"],
      component: FieldText,
    },
    {
      title: t("ticket"),
      key: "ticketName",
      width: "12%",
      sort: searchParams?.sort?.["ticketName"],
      component: FieldText,
    },
    ...(pipelineSlug !== EPipelineSlug.FIELD_SALES
      ? [
          {
            title: t("interview_type"),
            key: "interviewType",
            width:
              pipelineSlug === EPipelineSlug.CUSTOMER_SUCCESS ? "11%" : "16%",
            sort: searchParams?.sort?.["interviewType"],
            component: FieldText,
          } as TTabeField,
        ]
      : []),
    ...(pipelineSlug === EPipelineSlug.FIELD_SALES ||
    pipelineSlug === EPipelineSlug.CUSTOMER_SUCCESS
      ? [
          {
            title: t(
              pipelineSlug === EPipelineSlug.FIELD_SALES
                ? "corporation"
                : "job_title"
            ),
            key: "jobListing",
            sort: searchParams?.sort?.[
              pipelineSlug === EPipelineSlug.FIELD_SALES
                ? "corporation"
                : "jobListing"
            ],
            width: "20%",
            component: FieldText,
          } as TTabeField,
        ]
      : []),
    {
      title: t("submitter"),
      key: "submitterName",
      imageKey: "submitterProfilePicture",
      imageLabelKey: "submitterName",
      sort: searchParams?.sort?.["submitterName"],
      withImage: true,
      width: "12%",
      component: FieldText,
    },
    {
      title: t("action"),
      key: "action_list",
      width: "13%",
      icon: (
        <div className="text-primary-500 flex items-center gap-x-1 whitespace-nowrap">
          <EyeIcon />
          {t("view_notes")}
        </div>
      ),
      headerClassName: "text-center w-full",
      onClick: handleClickAction,
      component: FieldActionIcon,
    },
  ];

  const handleSort = useCallback(
    (newSort: TSort) => {
      const prevSorts = (searchParams?.sort ?? {}) as Record<
        string,
        TFinalSort
      >;

      const updatedSorts: Record<string, TFinalSort> = {
        ...prevSorts,
        [newSort.key]: {
          ...newSort,
          order: 0,
        },
      };

      const allNone = Object.values(updatedSorts).every(
        (item) => item.type === "none"
      );

      if (allNone) {
        if (newSort.key === "dateAndTime") {
          onSetSearchParams({
            sort: newSort,
          });
          return;
        }

        const defaultDateSort: TSort = {
          fieldName: "date_created",
          key: "dateAndTime",
          type: "desc",
        };

        onSetSearchParams({
          sort: defaultDateSort,
        });
        return;
      }

      onSetSearchParams({
        sort: newSort,
      });
    },
    [onSetSearchParams, searchParams?.sort]
  );

  const handleSearch = useCallback(
    (keyword: string) => {
      onSetSearchParams({
        keyword: keyword,
      });
    },
    [onSetSearchParams]
  );

  const handleFilter = useCallback(
    (filter: Partial<TGetNotesFilter>) => {
      onSetSearchParams({ ...filter });
    },
    [onSetSearchParams]
  );

  const handleCloseDetailModal = () => {
    setSelectedNote(undefined);
  };

  return {
    label,
    profileImage,
    profileName,
    profileAccountType,
    profileUniqueId,
    profileUrlPath,
    profileSlug,
    entityType,
    tab,
    pipelineSlug,
    selectedNote,
    searchParams,
    pagination,
    notes,
    isLoadingGetNotes,
    fields,
    selectedTab,
    onViewProfile: handleViewProfile,
    onChangePage: handleChangePage,
    onSort: handleSort,
    onChangeTab: handleChangeTab,
    onChangePerPage: handleChangePerPage,
    onSearch: handleSearch,
    onFilter: handleFilter,
    onChangeDate: handleChangeDate,
    onCloseDetailModal: handleCloseDetailModal,
  };
};
